import HistorialCambios from "components/HistorialCambios";
import { gql, useMutation, useQuery } from "@apollo/client";
import { useEffect, useState, useCallback, useMemo, useContext } from "react";

// Components
import { Card, Grid, Icon, Menu, MenuItem } from "@mui/material";
import Footer from "components/Footer";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import ModalDelete from "components/Modals/Delete";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DataTable from "components/Tables/DataTable";
import CustomPagination from "components/Pagination";
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";

import { getColor } from "utils/colors";
import ModalEmpresa from "./components/ModalEmpresa";
import ModalCambiarTasa from "./components/ModalCambiarTasa";
import dataEmpresa from "./data/dataEmpresa";
import { MessageManager } from "context";
import { UserContext } from "context/user";

function DeclaracionesJuradas() {
  const [rows, setRows] = useState([]);

  const [openMenu, setOpenMenu] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openModalAdd, setOpenModalAdd] = useState(false);
  const [openModalTasa, setOpenModalTasa] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [selectedDeclaracion, setSelectedDeclaracion] = useState(null);
  const [page, setPage] = useState(1);
  const [pagesInfo, setPagesInfo] = useState({ pages: 0, total: 0 });
  const [orderBy, setOrderBy] = useState({ campo: "id", orden: "DESC" });

  const { columns, getRows } = dataEmpresa;

  const { handleSnackbar } = useContext(MessageManager);
  const { user, verificarAcceso } = useContext(UserContext);

  const [deleteDeclaracionJurada] = useMutation(gql`
    mutation deleteDeclaracionJurada($id: ID!) {
      deleteDeclaracionJurada(id: $id) {
        id
        estado
      }
    }
  `);
  // Funciones con useCallback
  const handleOrderBy = useCallback((campo) => {
    setOrderBy((prev) => ({
      campo,
      orden: prev.campo === campo && prev.orden === "ASC" ? "DESC" : "ASC",
    }));
  }, []);

  const handleOpenMenu = useCallback((event, item) => {
    setAnchorEl(event.currentTarget);
    setOpenMenu(true);
    setSelectedDeclaracion(item);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null);
    setOpenMenu(false);
  }, []);

  const { loading, error, data, refetch } = useQuery(
    gql`
      query getDeclaracionesJuradas($search: String, $page: Int, $order: Order) {
        paginationInfo {
          pages
          total
        }
        declaracionesJuradas(
          search: $search
          pagination: { page: $page, limit: 10 }
          order: $order
        ) {
          id
          actividad
          estado
          idEmpresa
          tasa
          montoTasa
          empresa {
            estado
            id
            razonSocial
            cuit
            email
          }
          responsableTecnico {
            id
            nombre
            apellido
            matricula
          }
        }
      }
    `,
    {
      variables: {
        search: null,
        page: page,
        order: { field: orderBy.campo, order: orderBy.orden },
        // filter: {
        //   idResponsable: null,
        // }
      },
      fetchPolicy: "no-cache",
    }
  );

  const handleDeleteDeclaracionJurada = useCallback(async () => {
    if (!selectedDeclaracion?.id) return;

    try {
      await deleteDeclaracionJurada({ variables: { id: selectedDeclaracion.id } });
      await refetch();
      handleSnackbar("Declaración jurada eliminada correctamente", "success");
    } catch (e) {
      handleSnackbar(e.message || "Error al eliminar la declaración jurada", "error");
    }
  }, [deleteDeclaracionJurada, selectedDeclaracion, refetch, handleSnackbar]);

  const canReviewDeclaration = verificarAcceso(205);
  const external = ["Empresa", "Responsable"].includes(user?.tipoUser);

  const memoizedRows = useMemo(() => {
    if (data) {
      return getRows(data.declaracionesJuradas, handleOpenMenu);
    }
    return [];
  }, [data, getRows, handleOpenMenu]);

  useEffect(() => {
    if (data) {
      setRows(memoizedRows);
      setPagesInfo(data.paginationInfo);
    }
  }, [data, memoizedRows]);

  const estadoSeleccionado = selectedDeclaracion?.estado?.toLowerCase();

  // Render del menú contextual
  const renderMenu = (
    <Menu id="simple-menu" anchorEl={anchorEl} open={Boolean(openMenu)} onClose={handleCloseMenu}>
      {(estadoSeleccionado === "aceptada" || estadoSeleccionado === "presentada") &&
        canReviewDeclaration && (
          <MenuItem
            onClick={() => {
              setOpenModalTasa(true);
              handleCloseMenu();
            }}
          >
            <SoftBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ width: "100%" }}
            >
              <SoftTypography variant="p">Cambiar tasa</SoftTypography>
              <Icon fontSize="small" color="dark" sx={{ verticalAlign: "middle" }}>
                autorenew
              </Icon>
          </SoftBox>
        </MenuItem>
      )}
      <HistorialCambios modulo="ddjj" id={selectedDeclaracion?.id} nombre={[selectedDeclaracion?.empresa?.razonSocial, selectedDeclaracion?.actividad].filter(Boolean).join(" · ")} menu />
      <MenuItem
        onClick={() => {
          let actividadSeleccionada = selectedDeclaracion?.actividad
            ?.replace(/ /g, "-")
            .replace(/\//g, "_");
          window.location.href = `/declaracion/${selectedDeclaracion?.idEmpresa}/${selectedDeclaracion?.responsableTecnico?.id}/${actividadSeleccionada}/${selectedDeclaracion?.id}`;
          handleCloseMenu();
        }}
      >
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: "100%" }}
        >
          <SoftTypography variant="p">
            {estadoSeleccionado !== "borrador" || selectedDeclaracion?.empresa?.estado === "Irregular" ? "Ver" : "Ver/Editar"}
          </SoftTypography>
          <Icon fontSize="small" sx={{ verticalAlign: "middle", color: getColor("gobSecondary") }}>
            visibility
          </Icon>
        </SoftBox>
      </MenuItem>
      {estadoSeleccionado === "aceptada" && (
        <MenuItem
          onClick={() => {
            window.location.href = `/declaracion-pdf/${selectedDeclaracion?.id}`;
            handleCloseMenu();
          }}
        >
          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ width: "100%" }}
          >
            <SoftTypography variant="p">PDF</SoftTypography>
            <Icon fontSize="small" color="dark" sx={{ verticalAlign: "middle" }}>
              picture_as_pdf
            </Icon>
          </SoftBox>
        </MenuItem>
      )}
      {estadoSeleccionado === "borrador" && (external || verificarAcceso(206)) && (
        <MenuItem
          disabled={external && selectedDeclaracion?.empresa?.estado === "Irregular"}
          onClick={() => {
            setOpenModalDelete(true);
            handleCloseMenu();
          }}
        >
          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ width: "100%" }}
          >
            <SoftTypography variant="p">Borrar</SoftTypography>
            <Icon
              fontSize="small"
              sx={{ verticalAlign: "middle", color: getColor("gobSecondary") }}
            >
              delete
            </Icon>
          </SoftBox>
        </MenuItem>
      )}
    </Menu>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox>
          <Card>
            <Grid container spacing={2} p={3}>
              <Grid item xs={12}>
                <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                  <SoftTypography variant="h6" fontWeight="bold">
                    Declaraciones juradas
                  </SoftTypography>
                  {/* <SoftBox display="flex" alignItems="center" gap={2}>
                    <SoftInput
                      placeholder="Buscar..."
                      icon={{ component: "search", direction: "left" }}
                      onChange={handleSearch}
                      value={searchInput}
                    />
                    
                  </SoftBox> */}
                </SoftBox>
              </Grid>
            </Grid>
            <SoftBox>
              {loading ? (
                <Loading />
              ) : rows?.length > 0 ? (
                <DataTable columns={columns} rows={rows} order={orderBy} setOrder={handleOrderBy} />
              ) : (
                <SinDatos />
              )}
            </SoftBox>
            <CustomPagination
              loading={loading}
              length={rows.length}
              total={pagesInfo.total}
              pages={pagesInfo.pages}
              page={page}
              setPage={setPage}
              noCard
            />
          </Card>
          {renderMenu}
          <ModalEmpresa
            open={openModalAdd}
            handleClose={() => setOpenModalAdd(false)}
            refetch={refetch}
            idEmpresa={selectedDeclaracion?.id}
          />
          <ModalCambiarTasa
            open={openModalTasa}
            handleClose={() => setOpenModalTasa(false)}
            refetch={refetch}
            declaracion={selectedDeclaracion}
          />

          <ModalDelete
            open={openModalDelete}
            handleClose={() => setOpenModalDelete(false)}
            nombre={`la declaración jurada ${
              selectedDeclaracion?.id ? `#${selectedDeclaracion.id}` : "seleccionada"
            }`}
            functionDelete={handleDeleteDeclaracionJurada}
          />
        </SoftBox>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default DeclaracionesJuradas;
